import type { AgentTool, ToolContext, HostBridge, UserQuestion } from './tools';

export type { HostBridge, UserQuestion } from './tools';

/**
 * `AskUserQuestion` tool — lets the model pose a structured choice to a human.
 * Delegates to `ctx.host.ask` when a human is reachable.
 *
 * When NO human is reachable (`host.ask` absent — headless `-p`, a piped/redirected run, an edge
 * host), the question is NOT dropped and NOT guessed at: it parks on `ctx.needsInput`, and the
 * Agent ends the run with `finishReason:'needs_input'` carrying the question up to the CALLER.
 * The caller answers and resumes. Guessing silently is the failure mode this exists to prevent —
 * a delegated agent that picks for you on a decision that was never its to make.
 */
export const askUserQuestionTool: AgentTool = {
  name: 'AskUserQuestion',
  description:
    'Ask the user a multiple-choice question when a decision is genuinely theirs to make: an ambiguous or under-specified requirement, a fork you cannot resolve from context, a premise of the task that the evidence contradicts, or a risky/irreversible next step. Prefer this over guessing, "proceeding with best judgment", or quietly widening scope to make an unclear brief fit — a wrong silent decision is far more costly than a question. Returns their selection if a human is reachable; otherwise the run pauses and the question is handed to whoever delegated the task (you do not need to know which — just ask).',
  parameters: {
    type: 'object',
    required: ['question', 'options'],
    properties: {
      question: { type: 'string' },
      header: { type: 'string', description: 'short label for the question' },
      options: {
        type: 'array',
        items: { type: 'object', required: ['label'], properties: { label: { type: 'string' }, description: { type: 'string' } } },
      },
      multiSelect: { type: 'boolean' },
    },
  },
  async run(args: UserQuestion, ctx: ToolContext) {
    if (!ctx.host?.ask) {
      // No human on this side of the process — raise the question to the caller and stop the run.
      // Never block on a prompt nobody can see (that hangs until an external timeout reaps the turn).
      ctx.needsInput = args;
      return 'PAUSED — no interactive user is reachable from this run, so your question has been raised to the caller that delegated this task. The run stops here; do not guess and do not continue. Their answer will arrive as the next user message when the run resumes.';
    }
    const answer = ctx.host.ask(args); // human-blocking — park it so the wait doesn't count toward the timeout
    return ctx.parkHuman ? ctx.parkHuman(answer) : answer;
  },
};

/** Deterministic HostBridge for tests/dev: scripts answers + records what was asked. */
export class ScriptedHostBridge implements HostBridge {
  public asked: UserQuestion[] = [];
  public confirms: string[] = [];
  public events: Array<{ kind: string; message: string; data?: unknown }> = [];
  constructor(private answers: string[] = [], private confirmDefault = true) {}
  async ask(q: UserQuestion): Promise<string> {
    this.asked.push(q);
    return this.answers.shift() ?? q.options[0]?.label ?? '';
  }
  async confirm(prompt: string): Promise<boolean> {
    this.confirms.push(prompt);
    return this.confirmDefault;
  }
  notify(event: { kind: string; message: string; data?: unknown }): void {
    this.events.push(event);
  }
}

/** Node CLI HostBridge: prompts on stdin. (node-only host adapter; not edge-safe by design.) */
export class ConsoleHostBridge implements HostBridge {
  async ask(q: UserQuestion): Promise<string> {
    const rl = await import('node:readline/promises');
    const io = rl.createInterface({ input: process.stdin, output: process.stdout });
    try {
      const lines = [q.header ? `\n[${q.header}] ${q.question}` : `\n${q.question}`];
      q.options.forEach((o, i) => lines.push(`  ${i + 1}) ${o.label}${o.description ? ' — ' + o.description : ''}`));
      const ans = await io.question(lines.join('\n') + '\n> ');
      const n = Number(ans.trim());
      return Number.isInteger(n) && q.options[n - 1] ? q.options[n - 1].label : ans.trim();
    } finally {
      io.close();
    }
  }
  async confirm(prompt: string): Promise<boolean> {
    const rl = await import('node:readline/promises');
    const io = rl.createInterface({ input: process.stdin, output: process.stdout });
    try {
      const ans = await io.question(`\n${prompt} [y/N] `);
      return /^y(es)?$/i.test(ans.trim());
    } finally {
      io.close();
    }
  }
  notify(event: { kind: string; message: string }): void {
    console.error(`[${event.kind}] ${event.message}`);
  }
}
